export const AVATAR_CACHE_NAME = "cbta-avatar";

export function getAvatarCacheKey(rawUrl?: string | null): string | null {
	if (!rawUrl) {
		return null;
	}

	let url: URL;
	try {
		const base = typeof window !== "undefined" ? window.location.origin : "https://localhost";
		url = new URL(rawUrl, base);
	} catch {
		return null;
	}

	if (url.protocol !== "https:" && url.protocol !== "http:") {
		return null;
	}

	const host = url.hostname.toLowerCase();
	const isGoogleUserContent = host === "googleusercontent.com" || host.endsWith(".googleusercontent.com");
	const isGcsProfilePicture =
		(host === "storage.googleapis.com" || host.endsWith(".storage.googleapis.com")) &&
		url.pathname.includes("/profile-picture/");

	if (!isGoogleUserContent && !isGcsProfilePicture) {
		return null;
	}

	return `${url.origin}${url.pathname}`;
}

export function isExpiredGcsSignedUrl(rawUrl?: string | null): boolean {
	if (!rawUrl) {
		return false;
	}

	let url: URL;
	try {
		const base = typeof window !== "undefined" ? window.location.origin : "https://localhost";
		url = new URL(rawUrl, base);
	} catch {
		return false;
	}

	const googDate = url.searchParams.get("X-Goog-Date");
	const googExpires = url.searchParams.get("X-Goog-Expires");
	if (!googDate || !googExpires) {
		return false;
	}

	const match = /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/.exec(googDate);
	if (!match) {
		return false;
	}

	const issuedAtMs = Date.UTC(
		Number(match[1]),
		Number(match[2]) - 1,
		Number(match[3]),
		Number(match[4]),
		Number(match[5]),
		Number(match[6]),
	);
	const expiresSec = Number(googExpires);
	if (!Number.isFinite(issuedAtMs) || !Number.isFinite(expiresSec)) {
		return false;
	}

	return Date.now() >= issuedAtMs + expiresSec * 1000;
}

export async function warmAvatarCache(rawUrl?: string | null): Promise<void> {
	if (
		!rawUrl ||
		typeof window === "undefined" ||
		!("caches" in window) ||
		typeof fetch !== "function" ||
		(typeof navigator !== "undefined" && navigator.onLine === false) ||
		isExpiredGcsSignedUrl(rawUrl)
	) {
		return;
	}

	const cacheKey = getAvatarCacheKey(rawUrl);
	if (!cacheKey) {
		return;
	}

	try {
		const cache = await caches.open(AVATAR_CACHE_NAME);
		const existing = await cache.match(cacheKey, { ignoreVary: true });
		if (existing) {
			return;
		}

		const response = await fetch(rawUrl, { mode: "no-cors", credentials: "omit" });
		if (response && (response.ok || response.type === "opaque")) {
			await cache.put(cacheKey, response);
		}
	} catch {
		// Ignore cache/network failures; SW or fallback initial will handle it.
	}
}

export async function clearAvatarCache(): Promise<void> {
	if (typeof window === "undefined" || !("caches" in window)) {
		return;
	}

	try {
		await caches.delete(AVATAR_CACHE_NAME);
	} catch {
		// Ignore cache deletion errors.
	}
}
